import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Bus, User } from "lucide-react";
import { toast } from "sonner";

const TDIR = { pickup: "AM Home Pick-Up", dropoff: "PM Home Drop-Off", both: "Pick-Up + Drop-Off", none: "—" };
const DROP_PREF = { collected_stop: "Nearest bus stop", walk_home: "May walk from stop", door: "Bus reaches door" };

export default function CampRoster() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [camps, setCamps] = useState([]);
  const [campId, setCampId] = useState("");
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/camps").then((r) => {
      const cs = r.data.camps || [];
      setCamps(cs);
      if (cs.length) { setCampId(cs[0].id); setDate((cs[0].dates || [])[0] || ""); }
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  const camp = camps.find((c) => c.id === campId);

  const load = useCallback(() => {
    if (!campId || !date) return;
    api.get(`/staff/camp-roster?camp_id=${campId}&date=${date}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [campId, date]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const withTransport = rows.filter((r) => r.transport && r.transport !== "none");
  const dateLabel = date ? new Date(date + "T12:00").toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-navy text-white print:bg-white print:text-black">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10 print:hidden">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">CAMP ROSTER</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" onClick={() => nav("/staff")} className="text-white/70">Scanner</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/transport")} className="text-white/70">Transport</Button>
          <Button variant="ghost" size="sm" onClick={() => nav(["admin", "manager"].includes(user?.role) ? "/admin" : "/dashboard")} className="text-white/70"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }}>✕</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-5 print:p-0 print:max-w-none">
        <div className="flex flex-wrap items-end gap-3 mb-5 print:hidden">
          <div>
            <label className="text-xs text-white/50 block mb-1">Camp</label>
            <select value={campId} onChange={(e) => { setCampId(e.target.value); const c = camps.find((x) => x.id === e.target.value); setDate((c?.dates || [])[0] || ""); }} className="rounded-xl bg-white/10 border border-white/20 text-white px-3 py-2 text-sm" data-testid="roster-camp-select">
              {camps.map((c) => <option key={c.id} value={c.id} className="text-black">{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Day</label>
            <select value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl bg-white/10 border border-white/20 text-white px-3 py-2 text-sm" data-testid="roster-date-select">
              {(camp?.dates || []).map((d) => <option key={d} value={d} className="text-black">{new Date(d + "T12:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })}</option>)}
            </select>
          </div>
          <Button onClick={() => window.print()} className="bg-turquoise text-navy rounded-full font-bold" data-testid="roster-print-btn"><Printer className="w-4 h-4 mr-1" /> Print</Button>
        </div>

        <div className="mb-4 print:mb-6">
          <h1 className="font-display font-extrabold text-2xl print:text-black" data-testid="roster-title">{camp?.name || "Camp"} — {dateLabel}</h1>
          <p className="text-white/60 text-sm print:text-black">{rows.length} explorer{rows.length === 1 ? "" : "s"} · {withTransport.length} needing transport</p>
        </div>

        {rows.length === 0 && <p className="text-white/40 text-sm print:text-black" data-testid="roster-empty">No explorers registered for this day.</p>}

        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4 print:border-slate-300 print:rounded-none print:bg-transparent print:break-inside-avoid" data-testid={`roster-row-${i}`}>
              <div className="flex items-start gap-3">
                <div className="w-6 text-white/40 font-mono text-sm print:text-slate-500">{i + 1}.</div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-white print:text-black">{r.child_name}{r.preferred_name ? ` (${r.preferred_name})` : ""} <span className="font-mono text-turquoise text-xs print:text-slate-500">{r.explorer_id}</span></p>
                  {r.allergies && <p className="text-[12px] text-coral print:text-red-700">⚠ Allergies: {r.allergies}</p>}
                  {r.medical && <p className="text-[12px] text-coral/80 print:text-red-700">⚕ {r.medical}</p>}
                  <p className="text-[12px] text-white/60 print:text-black mt-0.5">Parent: {r.parent_name || "—"} · {r.parent_phone || "—"}</p>
                  {r.transport && r.transport !== "none" ? (
                    <div className="mt-1.5 text-[12px] text-white/80 print:text-black bg-turquoise/10 print:bg-transparent rounded-lg px-2 py-1.5 flex flex-col gap-0.5">
                      <span className="font-semibold text-turquoise print:text-black flex items-center gap-1"><Bus className="w-3.5 h-3.5" /> {TDIR[r.transport]}</span>
                      {(r.transport === "pickup" || r.transport === "both") && <span>AM pick-up: {r.pickup_address || "—"}{r.pickup_time ? ` @ ${r.pickup_time}` : ""}</span>}
                      {(r.transport === "dropoff" || r.transport === "both") && <span>PM drop-off: {r.dropoff_address || "—"}{r.dropoff_preference ? ` · ${DROP_PREF[r.dropoff_preference] || r.dropoff_preference}` : ""}{r.receiver_name ? ` · to ${r.receiver_name}` : ""}</span>}
                    </div>
                  ) : (
                    <p className="text-[12px] text-white/40 print:text-slate-500 mt-1 flex items-center gap-1"><User className="w-3.5 h-3.5" /> Parent drop-off & collect</p>
                  )}
                  {r.payment_status !== "confirmed" && <span className="inline-block mt-1 text-[10px] font-bold text-gold bg-gold/15 print:bg-transparent print:border print:border-gold rounded-full px-2 py-0.5">payment {r.payment_status}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
